import { useApi } from '@cleartax/wg-library-hooks';
import { GetServerSidePropsContext } from 'next';
import React, { useEffect, useRef } from 'react';
import { NEXT_REDIRECT_OBJECT_HOMEPAGE } from '../../../../config/constants';
import { getAuthenticationStatus, trackAction } from '../../../../shared';
import { SectionProps } from '../../../../shared/components/layout-components/navigation/types';
import { DefaultLayout } from '../../../../shared/layouts/default-layout';
import { Meta } from '../../../../shared/layouts/meta';
import { EVENTS } from '../../../../shared/utils/tracker/configs';
import { getPriceDetails, getProductDetails } from '../api/index.api';
import { TPriceDetails } from '../api/pricingTypes';
import { TPlanDetails } from '../api/types';
import { AboutSection } from './components/page-sections/about';
import { DocumentsRequired } from './components/page-sections/documents-required/index';
import { FAQSection } from './components/page-sections/faq';
import { HowItsDone } from './components/page-sections/how-its-done';
import { Review } from './components/page-sections/reviews';
import { SeoContent } from './components/page-sections/seo-content';
import { ServicesIncluded } from './components/page-sections/services-included';
import { WhoShouldBuy } from './components/page-sections/who-should-buy';
import { PlanDetailsComponent } from './components/planDetailsComponent';
import { PlanDetailsProvider } from './planDetailsContext';

interface IPlanPage {
  planDetails: TPlanDetails;
}

const PlanPage: React.FC<IPlanPage> = ({ planDetails }) => {
  const sections = [
    <AboutSection
      key={'about'}
      id={'about'}
      name={'About this plan'}
      ref={useRef<HTMLDivElement>(null)}
    />,
    <ServicesIncluded
      key={'services-included'}
      id={'services-included'}
      name={'Services Included'}
      ref={useRef<HTMLDivElement>(null)}
    />,
    <WhoShouldBuy
      key={'who-should-buy'}
      id={'who-should-buy'}
      name={'Who Should Buy'}
      ref={useRef<HTMLDivElement>(null)}
    />,
    <HowItsDone
      key={'how-its-done'}
      id={'how-its-done'}
      name={`How It's Done`}
      ref={useRef<HTMLDivElement>(null)}
    />,
    <DocumentsRequired
      key={'documents-required'}
      id={'documents-required'}
      name={'Documents Required'}
      ref={useRef<HTMLDivElement>(null)}
    />,
    <SeoContent
      key={'seo-content'}
      id={'seo-content'}
      name={planDetails.seoContentBlockTitle}
      ref={useRef<HTMLDivElement>(null)}
    />,
    <FAQSection
      key={'faq'}
      id={'faq'}
      name={'FAQs'}
      ref={useRef<HTMLDivElement>(null)}
    />,
    <Review
      key={'review'}
      id={'review'}
      name={'Review'}
      ref={useRef<HTMLDivElement>(null)}
    />,
  ] as SectionProps[];
  const {
    seoName,
    seoSlug,
    seoDescription,
    seoKeywords,
    noindex,
    ogImage,
    planId,
  } = planDetails;
  useEffect(() => {
    trackAction(EVENTS.pageView, '', { label: `services_${seoSlug}` });
  }, []);
  const getPlanPriceDetailsApi = useApi<TPriceDetails>(
    {},
    getPriceDetails,
    true
  );
  useEffect(() => {
    getPlanPriceDetailsApi.request(planId);
  }, []);

  return (
    <DefaultLayout
      meta={
        <Meta
          title={seoName}
          canonical={`/services/${seoSlug}/p`}
          description={seoDescription}
          keywords={seoKeywords}
          noindex={noindex}
          ogimage={ogImage}
        />
      }
    >
      <PlanDetailsProvider
        value={{
          ...planDetails,
          getPlanPriceDetailsApi,
        }}
      >
        <PlanDetailsComponent sections={sections} />
      </PlanDetailsProvider>
    </DefaultLayout>
  );
};

export default PlanPage;

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { planSeoSlug } = context.query;
  const [response, auth] = await Promise.all([
    getProductDetails(planSeoSlug as string),
    getAuthenticationStatus(context.req.headers.cookie),
  ]);

  const isPlanInactive = response && !response.active;

  if (!response || isPlanInactive) return NEXT_REDIRECT_OBJECT_HOMEPAGE;

  if (response.seoSlug && response.seoSlug !== planSeoSlug) {
    return {
      redirect: {
        permanent: false,
        destination: `/services/${response.seoSlug}/p`,
      },
    };
  }

  return {
    props: {
      planDetails: response,
      ...auth,
    },
  };
}
